import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Controller, Control, useFieldArray, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { SmartRetryFormData } from '../page'
import { generateRetryId } from './utils'

interface CustomRetryScheduleProps {
	control: Control<SmartRetryFormData>
	setValue: UseFormSetValue<SmartRetryFormData>
	errors: FieldErrors<SmartRetryFormData>
	config: NonNullable<SmartRetryFormData['config']>
}

export const CustomRetrySchedule = ({
	control,
	setValue,
	errors,
	config,
}: CustomRetryScheduleProps) => {
	const { t } = useTranslation('paymentFlows')
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'config.customRetries',
		shouldUnregister: false,
	})

	const handleAddRetry = () => {
		append({
			id: generateRetryId(),
			value: 1,
			unit: 'days',
			description: '1 day later',
		})
	}

	const handleValueChange = (index: number, value: number) => {
		const unit = config.customRetries[index]?.unit || 'days'
		setValue(`config.customRetries.${index}.description`, `${value} ${unit} later`)
	}

	const handleUnitChange = (index: number, unit: 'days' | 'weeks') => {
		const value = config.customRetries[index]?.value || 1
		setValue(`config.customRetries.${index}.description`, `${value} ${unit} later`)
	}

	return (
		<div>
			<div className="mb-3 flex items-center justify-between">
				<Label className="text-sm font-medium">
					{t('smartRetry.fields.customRetries.label')}
				</Label>
				<Button type="button" variant="outline" size="sm" onClick={handleAddRetry}>
					<Plus className="mr-1 size-4" />
					{t('smartRetry.fields.customRetries.add')}
				</Button>
			</div>

			<div className="space-y-3">
				{fields.map((field, index) => (
					<Card key={field.id} className="p-4">
						<div className="flex flex-col items-start gap-4 sm:flex-row">
							<div className="grid w-full flex-1 grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
								<div>
									<Label className="text-xs text-muted-foreground">
										{t('smartRetry.fields.customRetries.value')}
									</Label>
									<Controller
										name={`config.customRetries.${index}.value`}
										control={control}
										render={({ field: fieldController }) => (
											<Input
												type="number"
												{...fieldController}
												value={fieldController.value ?? 1}
												onChange={(e) => {
													const value = Math.max(1, parseInt(e.target.value) || 1)
													fieldController.onChange(value)
													handleValueChange(index, value)
												}}
												min={1}
												className="mt-1"
											/>
										)}
									/>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										{t('smartRetry.fields.customRetries.unit')}
									</Label>
									<Controller
										name={`config.customRetries.${index}.unit`}
										control={control}
										render={({ field: fieldController }) => (
											<Select
												value={fieldController.value}
												onValueChange={(value) => {
													fieldController.onChange(value as 'days' | 'weeks')
													handleUnitChange(index, value as 'days' | 'weeks')
												}}
											>
												<SelectTrigger className="mt-1">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="days">
														{t('smartRetry.fields.customRetries.units.days')}
													</SelectItem>
													<SelectItem value="weeks">
														{t('smartRetry.fields.customRetries.units.weeks')}
													</SelectItem>
												</SelectContent>
											</Select>
										)}
									/>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										{t('smartRetry.fields.customRetries.description')}
									</Label>
									<Controller
										name={`config.customRetries.${index}.description`}
										control={control}
										render={({ field: fieldController }) => (
											<Input
												{...fieldController}
												value={fieldController.value || ''}
												className="mt-1"
												placeholder={t('smartRetry.fields.customRetries.descriptionPlaceholder')}
											/>
										)}
									/>
								</div>
							</div>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => remove(index)}
								disabled={fields.length <= 1}
								className="mt-6"
							>
								<Trash2 className="size-4 text-destructive" />
							</Button>
						</div>
						{errors.config?.customRetries?.[index] && (
							<p className="mt-1 text-sm text-red-500">
								{errors.config.customRetries[index]?.value?.message ||
									errors.config.customRetries[index]?.description?.message}
							</p>
						)}
					</Card>
				))}
			</div>
			{errors.config?.customRetries && (
				<p className="mt-2 text-sm text-red-500">{errors.config.customRetries.message}</p>
			)}
		</div>
	)
}

